/**
 * showIf — conditional-display evaluation for prescreen criteria.
 *
 * Prescreens reuse the FORMS show_if vocabulary (a flat {question_key,
 * operator, value} or a {match, conditions[]} group, evaluated by
 * publicFormShared.isVisible — hidden while the referenced answer is
 * unanswered). One prescreen-specific bridge lives here: prescreen `boolean`
 * answers are real true/false, while the condition builder stores the forms
 * yes_no vocabulary ('yes'/'no' strings) — so boolean answers normalize to
 * 'yes'/'no' before matching. Everything else (choice option values, numbers,
 * height-as-total-inches, text, dates) compares under isVisible's existing
 * string-coercion tolerance.
 *
 * Key space is the CALLER's: the builder preview evaluates draft criteria
 * keyed by `ref`; the runner evaluates published criteria keyed by
 * `question_key`. Conditions always reference the same key space as the list
 * they live in.
 *
 * Semantics (forms parity, deliberately): visibility does NOT cascade — a
 * hidden question's saved answer still feeds conditions that reference it,
 * exactly as the form surfaces behave. Hidden questions are excluded from
 * verdict tallies by the consumers (they filter to the visible list).
 */
import { isVisible, ShowIfRule } from '../forms/publicFormShared';

export interface ShowIfCriterionLike {
  /** ref (draft) or question_key (published). */
  key: string;
  answer_type: string;
  show_if?: ShowIfRule | null;
}

/** Answers map with prescreen booleans translated to the condition
 *  vocabulary ('yes'/'no'); every other value passes through unchanged. */
export function normalizeShowIfAnswers(
  list: ShowIfCriterionLike[],
  answers: Record<string, any>,
): Record<string, any> {
  const boolKeys = new Set(list.filter((c) => c.answer_type === 'boolean').map((c) => c.key));
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(answers)) {
    if (boolKeys.has(key) && typeof value === 'boolean') out[key] = value ? 'yes' : 'no';
    else out[key] = value;
  }
  return out;
}

/** The subset of `list` currently visible given the (resolved) answers. */
export function visibleShowIfKeys(
  list: ShowIfCriterionLike[],
  answers: Record<string, any>,
): Set<string> {
  const normalized = normalizeShowIfAnswers(list, answers);
  const out = new Set<string>();
  for (const c of list) {
    if (isVisible({ show_if: c.show_if }, normalized)) out.add(c.key);
  }
  return out;
}
